import React, { useEffect, useState, useRef, ReactNode } from 'react';
import { MemoryHistory } from 'history';
import { useSelector, useDispatch } from 'react-redux';
import config from 'config';
import { IntlProvider } from 'react-intl';
import textToSpeechManager, {
  initializeTextToSpeechManager,
} from 'support/tts/TextToSpeechManager';

import { usePlatformLibrary } from 'hooks/platform';
import { initializeTTS } from 'hooks/platformTTS';
import { useDeeplink } from 'hooks/deeplink';
import { useSystemCC, useDeviceCC } from 'hooks/closedCaption';
import { useScript } from 'hooks/head';

import device from 'common/support/device';

import splashSports from 'assets/images/CBS_sports_splash_logo.svg';
import splashNews from 'assets/images/CBS_news_splash_logo.svg';

import { startUp } from 'actions/startup';

import ErrorModal from 'components/common/ErrorModal';
import WithIntl from 'components/common/WithIntl';
import { useHistory } from 'react-router-dom';
import { navigateTo } from 'support/navHelper';
import { VideoCtvRedux } from 'store-types';

interface SplashWrapperProps {
  children: ReactNode;
}

const SplashWrapper = ({ children }: SplashWrapperProps) => {
  const [isDataReady, setIsDataReady] = useState(false);
  const dispatch = useDispatch();
  useEffect(() => {
    const startUpAsync = async () => {
      await dispatch(startUp());
      setIsDataReady(true);
    };

    startUpAsync();
  }, [dispatch]);

  useEffect(() => {
    window.utag_data = {};
  }, []);

  // Load utag analytics library
  const utagSrc = `https://tags.tiqcdn.com/utag/cbsi/${config.analytics.utagUrl.platformPath}/${config.analytics.utagUrl.env}/utag.js`;
  const [utagLoaded] = useScript(utagSrc);

  let splashSrc = splashSports;
  // Detect Brand Id in order to load correct splash screen
  if (config.buildBrandId === 'cbs-news') {
    splashSrc = splashNews;
  }

  // We detect the platform before initialization because we want to determine the correct platform
  // at runtime in order to accurately load the correct platform libraries.
  const buildPlatform = config.platformName;
  const platform = device.detectPlatform(buildPlatform);
  const [platformLibraryLoaded, platformLibraryError] = usePlatformLibrary(
    platform,
  );

  const startupError = useSelector((reduxState: VideoCtvRedux) => reduxState.startup.error);
  const locale = useSelector((reduxState: VideoCtvRedux) => reduxState.localization.locale);
  const messages = useSelector((reduxState: VideoCtvRedux) => reduxState.localization.messages);

  const [deviceInitialized, setDeviceInitialized] = useState(false);
  // Temp fix, remove later
  useEffect(() => {
    let isUnmounted = false;
    (async () => {
      if (platformLibraryLoaded) {
        device.initializePlatform(platform);
        await initializeTTS(platform);
        if (isUnmounted) {
          return;
        }
        setDeviceInitialized(true);
      }
    })();
    return () => {
      isUnmounted = true;
    };
  }, [platformLibraryLoaded, platform]);

  useEffect(() => {
    let ttsLoadingText;
    if (platformLibraryLoaded && !!messages) {
      // intl localization is not yet ready.  Directly access the messages instead.
      ttsLoadingText = messages['splashwrapper.tts.loading'];
      if (ttsLoadingText) {
        ttsLoadingText = ttsLoadingText.replace('{ brand }', config.brand.name); // manually replace this since intl localization is not yet ready
        textToSpeechManager.add(ttsLoadingText);
        textToSpeechManager.speak();
      }
    }
  }, [messages, platformLibraryLoaded]);

  const error = startupError || platformLibraryError;
  const renderChildren =
    platformLibraryLoaded && deviceInitialized && isDataReady && utagLoaded;

  const hasRedirected = useRef(false);

  useDeviceCC();
  useSystemCC(platformLibraryLoaded);
  // eslint-disable-next-line no-unused-vars
  const history = useHistory() as MemoryHistory;
  useDeeplink(platformLibraryLoaded && platform, target => {
    navigateTo(history, target);
    hasRedirected.current = true;
  });

  return !renderChildren ? (
    <img
      className="w-100 h-100"
      data-testid="splash-image"
      src={splashSrc}
      alt=""
    />
  ) : (
    <IntlProvider
      locale={`${locale}`}
      defaultLocale="en-US"
      messages={messages}
    >
      {!error && children}

      <WithIntl
        render={intl => {
          initializeTextToSpeechManager(intl);
          return (
            <ErrorModal
              isOpen={!!error}
              title={intl.formatMessage({
                id: 'splashwrapper.error.title',
                defaultMessage: 'Oops!',
              })}
              message={intl.formatMessage({
                id: 'splashwrapper.error.message',
                defaultMessage:
                  'Something went wrong. Please close and restart the app.',
              })}
              data-testid="splash-wrapper-error-modal"
            />
          );
        }}
      />
    </IntlProvider>
  );
};

export default SplashWrapper;
